import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { oktaAuth } from '../auth';

const isMock = import.meta.env.VITE_OKTA_MOCK === 'true';

const isDev = import.meta.env.VITE_OKTA_ISSUER?.includes('oktapreview');
// Stage and prod share the same Okta tenant (adobe.okta.com), so the
// issuer can't tell them apart — the hostname can. The stage client's
// token only carries the STAGE groups, so gating stage on the prod
// group locks out every stage user.
const isStage = window.location.hostname.includes('.stage.');
const APP_GROUP = isDev
  ? 'GRP-APS-AJO-DESIGN-AGENT-DEV-USERS'
  : isStage
    ? 'GRP-APS-AJO-DESIGN-AGENT-STAGE-USERS'
    : 'GRP-APS-AJO-DESIGN-AGENT-USERS';

export default function CallbackPage() {
  const navigate = useNavigate();

  useEffect(() => {
    if (isMock) {
      const t = setTimeout(() => navigate('/'), 500);
      return () => clearTimeout(t);
    }

    // Canonical Adobe Agentic Apps flow (Confluence section 4.3):
    // CallbackPage reads the groups claim and checks the app-specific
    // IAM group. We do NOT gate on portal group membership here — the
    // doc's two-tier model puts that concern on the portal SPA, not on
    // each individual app, and adding a portal-bounce here just turned
    // the previously-valid flow into a redirect loop.
    oktaAuth
      .handleLoginRedirect()
      .then(() => {
        const idToken = oktaAuth.getIdToken();
        const claims = idToken ? oktaAuth.token.decode(idToken).payload : {};
        const groups = ((claims as Record<string, unknown>).groups as string[]) ?? [];

        if (!groups.includes(APP_GROUP)) {
          navigate(`/access-denied?group=${encodeURIComponent(APP_GROUP)}`);
          return;
        }
        navigate('/');
      })
      .catch(() => {
        // If Okta bounced us back with an explicit OAuth error (e.g.
        // access_denied for a user not assigned to the client app),
        // re-triggering sign-in just loops through Okta sub-second
        // forever — surface the denial instead.
        const oauthError = new URLSearchParams(window.location.search).get('error');
        if (oauthError) {
          navigate(`/access-denied?group=${encodeURIComponent(APP_GROUP)}`);
          return;
        }
        // Token exchange failed — send the user back through Okta so
        // they can try again. Falling back to the portal here was the
        // root cause of the cross-origin loop the user was seeing.
        oktaAuth.signInWithRedirect();
      });
  }, [navigate]);

  return null;
}
